import { entries, isUndefined, memoize, toNumber } from 'lodash-es';
import { Field } from '../../../services/form.service';
import { PromptFieldEntry } from './types';
import { blank, is_interval, OptionalValue, str_split, spf, data_is_false, data_get } from '../../../shared/utils/common';
import { TranslateService } from '@ngx-translate/core';

/// list of field labels that are exempted as checklist field entry
const CHECKLISTS_EXCLUDED_FIELD_ENTRY_FIELD_LABELS = [
  'phone',
  'address_geolocation',
  'attributes',
  'custom_attributes',
  'custom_field',
  'job_number',
  'status',
  'email_address',
  'invoicing_type',
  'priority',
  'job_type',
  'created_at',
  'created_by',
  'updated_at',
  'modified_by',
  'last_inspection_date',
  'is_custom_location',
  'is_invoiced',
  'amount_to_invoice',
  'amount_actually_invoiced',
  'external_id',
  'tags',
  'work_order',
  'hierarchical_view',
  'geolocation_point',
  'geolocation',
  'checklist_report_format',
];

/// List of field types that are exempted as checklist field entry
const CHECKLISTS_EXCLUDED_FIELD_ENTRY_FIELD_TYPES = [
  'address',
  'relate',
];

export const identify_module_group_name = (moduleName: string): PromptFieldEntry['group_name'] => {
  let groupName: PromptFieldEntry['group_name'] = 'ungrouped';

  if (moduleName == 'assets') {
    groupName = 'asset_fields';
  } else if (moduleName == 'sites') {
    groupName = 'site_fields';
  } else if (moduleName == 'opportunities') {
    groupName = 'opportunity_fields';
  } else if (moduleName == 'custom_asset_types') {
    groupName = 'custom_asset_fields';
  } else if (moduleName == 'jobs') {
    groupName = 'job_fields';
  }

  return groupName;
}

export const format_module_field_entries = (fields: Record<string, Field>, moduleName: string): PromptFieldEntry[] => {
  let output: PromptFieldEntry[] = [];

  for (const [name, metadata] of entries(fields)) {
    /// exclude if the the given field name or its type belongs to the list
    /// of types/names that are not supported
    if (CHECKLISTS_EXCLUDED_FIELD_ENTRY_FIELD_LABELS.includes(metadata.label)
      || CHECKLISTS_EXCLUDED_FIELD_ENTRY_FIELD_TYPES.includes(metadata.type)
    ) {
      continue;
    }

    if (! isUndefined(metadata.allow_as_field_entry_in_checklists)
      && data_is_false(data_get(metadata, 'allow_as_field_entry_in_checklists'))
    ) {
      continue;
    }

    output.push({
      id: spf('%s-%s', {
        args: [
          moduleName,
          name,
        ]
      }),
      name: metadata.label,
      group_name: identify_module_group_name(moduleName),
    });
  }

  return output;
}

export const translate_period = memoize((value: OptionalValue<string>, args: {
  translator: TranslateService,
}): OptionalValue<string> => {
  if (blank(value)) {
    return null;
  }

  if (! is_interval(value)) {
    return args.translator.instant(value);
  }

  const [size, unit] = str_split(value, {
    delimiter: '_',
  });

  if (toNumber(size) == 1) {
    return args.translator.instant(spf('every_%s', {
      args: [unit],
    }));
  }

  const key = spf('every_n_%s', {
    args: [unit],
  });

  return args.translator.instant(key, {
    'number': size,
  });
});